import { useCallback, useMemo, useState } from "react";
import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import { QSharedFilterRFC } from "@coreapp/CoreApp/QSharedFilterRFC";

export type SharedFilterDoc = {
  _id: string;
  chartId: string;
  type: "filter" | "highlight";
  owner: { username: string; displayName: string };
  definition: any;
  sharedWith: Array<{
    type: "user" | "group";
    id: string;
    displayName: string;
    role: "collaborator" | "viewer";
    sharedAt: string;
    sharedBy: string;
  }>;
  userRole: "owner" | "collaborator" | "viewer";
  createdAt: string;
  updatedAt: string;
};

type UseSharedFilterControllerResult = {
  sharedFilters: SharedFilterDoc[];
  sharedHighlights: SharedFilterDoc[];
  isBeingShared: (filterId: string) => boolean;
  loading: boolean;
  fetchSharedItems: (type?: "filter" | "highlight") => void;
  deleteShared: (filterId: string) => Promise<boolean>;
};

export const useSharedFilterController = (): UseSharedFilterControllerResult => {
  const [sharedFilters, setSharedFilters] = useState<SharedFilterDoc[]>([]);
  const [sharedHighlights, setSharedHighlights] = useState<SharedFilterDoc[]>([]);
  const [mySharedIds, setMySharedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSharedItems = useCallback((type?: "filter" | "highlight") => {
    const wsMgr = GetWSMgr();
    const doc = GetDoc();
    if (!wsMgr || !doc) return;

    const chartId = doc.GetChartID();
    if (!chartId) return;

    const userGroupId = wsMgr.GetUserInfo()?.GetUserGroupID?.() ?? "";
    const groupIds = userGroupId ? [userGroupId] : [];

    setLoading(true);

    QSharedFilterRFC.GetSharedFilters(wsMgr, chartId, type, groupIds)
      .then((raw: any) => {
        const response = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (response?.success) {
          const items = response.sharedWithMe || [];
          if (!type || type === "filter") {
            setSharedFilters(items.filter((item: SharedFilterDoc) => item.type === "filter"));
          }
          if (!type || type === "highlight") {
            setSharedHighlights(items.filter((item: SharedFilterDoc) => item.type === "highlight"));
          }
          setMySharedIds(response.mySharedIds || []);
        }
      })
      .catch((err: any) => {
        if (!err?.isAborted) {
          console.error("Failed to fetch shared filters:", err);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const deleteShared = useCallback(async (filterId: string): Promise<boolean> => {
    const wsMgr = GetWSMgr();
    if (!wsMgr) return false;

    try {
      const raw = await QSharedFilterRFC.DeleteSharedFilter(wsMgr, filterId);
      const response = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (response?.success) {
        setSharedFilters(prev => prev.filter(f => f._id !== filterId));
        setSharedHighlights(prev => prev.filter(h => h._id !== filterId));
        setMySharedIds(prev => prev.filter(id => id !== filterId));
        return true;
      }
    } catch (err) {
      console.error("Failed to delete shared filter:", err);
    }
    return false;
  }, []);

  const mySharedIdSet = useMemo(() => new Set(mySharedIds), [mySharedIds]);
  const isBeingShared = useCallback(
    (filterId: string) => mySharedIdSet.has(filterId),
    [mySharedIdSet]
  );

  return {
    sharedFilters,
    sharedHighlights,
    isBeingShared,
    loading,
    fetchSharedItems,
    deleteShared
  };
};
